import EmailIcon from '@mui/icons-material/Email';
import SyncIcon from '@mui/icons-material/Sync';
import { Card, CardContent, Checkbox, Chip, Container, FormControlLabel, FormGroup, IconButton, Stack, Typography, Box } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useStoreState } from 'pullstate';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SearchField from "../../components/SearchField.jsx";
import dataGridStyles from '../../components/styles/dataGridStyles.js';
import { AsyncIconButton } from '../../components/styles/EmployeesList.styles.js';
import { AppStore } from '../../stores/AppStore.js';
import { api } from '../../utils/apiFunctions';
import employeesColumns from '../../utils/employeesColumns.js';
import { getEmployees } from '../../utils/getData.js';
import useDebouncedValue from '../../utils/useDebouncedValue.js'

const ListUsersWithDataGrid = () => {
    const employees = useStoreState(AppStore, s => s.employees);
    const strengths = useStoreState(AppStore, s => s.strengths);
    const searchText = useStoreState(AppStore, s => s.searchText)
    const debouncedSearchText = useDebouncedValue(searchText, 100);
    const etroTeam = useStoreState(AppStore, s => s.etroTeam)
    const user = useStoreState(AppStore, s => s.user)
    const openAlert = useStoreState(AppStore, s => s.snackbar.openAlert)
    const strengthsChecked = useStoreState(AppStore, s => s.strengthsChecked)
    const missingInfo = useStoreState(AppStore, s => s.missingInfo)
    const [emailsSelected, setEmailsSelected] = useState([])
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(true)
    const [filterUsed, setFilterUsed] = useState(false)
    const [lastRunGetUsers, setLastRunGetUsers] = useState('')
    const location = useLocation()
    const currentPath = location.pathname
    const navigate = useNavigate()
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 10,
        page: 0,
    });
    const [rowSelectionModel, setRowSelectionModel] = React.useState([]);
    const DATE_FORMAT_OPTIONS = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    };
    const handleIconClick = (event) => {
        const iconButton = event.currentTarget;
        iconButton.classList.add('rotate');

        try {
            api.get('/users/update-users-from-google').then((res) => {
                if (res.status === 200) {
                    openAlert('Users updated successfully', 'success')
                    const lastRun = new Date().toLocaleDateString('en-ca', DATE_FORMAT_OPTIONS)
                    setLastRunGetUsers(lastRun)
                    getEmployees()
                } else {
                    openAlert('Error updating users', 'error')
                }

                iconButton.classList.remove('rotate');
            })
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const getChip = () => {
        if (etroTeam || strengthsChecked || missingInfo || searchText?.length || filterUsed) {
            return <Chip label="Filtered" variant="filled" color='info' />
        }
    }

    const usersDataFixed = useMemo(() => {
        if (!employees || employees.length === 0) return []
        const query = (debouncedSearchText || "").toLowerCase().trim();

        // Default view, filtered by search text, etro team, and suspended
        let usersEtroTeam = employees.filter(employee => (
            (
                (employee?.work_location?.building_name || '').toLowerCase().includes((query || '').toLowerCase()) ||
                (employee?.full_name || '').toLowerCase().includes((query || '').toLowerCase()) ||
                (employee?.coach || '').toLowerCase().includes((query || '').toLowerCase()) ||
                (employee?.title?.title || '').toLowerCase().includes((query || '').toLowerCase()) ||
                (employee?.department?.department || '').toLowerCase().includes((query || '').toLowerCase())
            ) &&
            employee?.etro_team !== etroTeam &&
            !employee?.suspended
        ))

        // Filter based on "Not ETRO Team", "No Clifton Strengths", "Missing Info" checkboxes
        usersEtroTeam = usersEtroTeam?.filter(employee => {
            let userStrengths = strengths?.find(strength => strength.user_id === employee.id)
            let checkingMissingInfo = (
                (!(employee.coach) || employee.coach === '' ) ||
                (!(employee.department) || employee.department === '' ) ||
                (!(employee.title) || employee.title === '' ) ||
                (!(employee.work_location) || employee.work_location === '' ) ||
                (!(employee.phone) || employee.phone === '' )
            ) && employee.id !== '101892955716787254375' // Exclude Mike

            if ( etroTeam && employee.etro_team === 1 )
                return false;
            if ( strengthsChecked && userStrengths !== undefined )
                return false;
            if ( missingInfo && !checkingMissingInfo )
                return false;
            return true;
        })

        const users = usersEtroTeam?.map(employee => ({
            id: employee.id,
            is_gd_admin: employee?.is_gd_admin,
            is_delegated_admin: employee?.is_delegated_admin,
            full_name: employee?.full_name ?? '',
            title: employee?.title?.title ?? '',
            department: employee?.department?.department ?? '',
            coach: employee?.coach ?? '',
            work_location: employee?.work_location?.building_name ?? '',
            phone: employee?.phone ?? '',
            photo_url: employee?.photo_url ?? '',
            suspended: employee?.suspended ?? ''
        }))
        setLoading(false);
        return users
    }, [employees, debouncedSearchText, etroTeam, missingInfo, strengthsChecked, strengths])

    const getCronLogs = async () => {
        try {
            const cronlogs = await api.get('/cronlogs')
            const cronlogFiltered = cronlogs?.data.filter(cron => cron.name === 'bringing users from google')
            const theMostRecentLog = cronlogFiltered?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
            const lastRun = theMostRecentLog?.created_at ? new Date(theMostRecentLog.created_at).toLocaleDateString('en-ca', DATE_FORMAT_OPTIONS) : ''
            setLastRunGetUsers(lastRun)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        getCronLogs()
    }, [])

    useEffect(() => {
        if (rows?.length > 0) {
            setLoading(false)
        }
    }, [rows])

    useEffect(() => {
        setRows(usersDataFixed)
    }, [usersDataFixed, employees])

    const handleUserToProfile = useCallback((user) => {

        if (currentPath === '/') {
            navigate(`employee/profile/${user.id}`)
        } else {
            navigate(`profile/${user.id}`)
        }
    }, [navigate, currentPath]);

    useEffect(() => {
        setPaginationModel((prev) => ({ ...prev, pageSize: parseInt(localStorage.getItem('pageSize')), page: 0 }))
    }, []);

    const setNewPageSize = useCallback((pageSize) => {
        if (pageSize) {
            localStorage.setItem('pageSize', pageSize);
            return
        }
        localStorage.setItem('pageSize', parseInt(localStorage.getItem('pageSize')) || 10);
    }, [])

    useEffect(() => {
        setNewPageSize()
    }, [])

    const getEmailsSelected = useCallback((rowSelected) => {
        const emails = employees.filter((row) => rowSelected.includes(row.id)).map((row => row.email))
        setEmailsSelected(emails)
    }, [employees])

    return (
        <Container maxWidth="xl" sx={{ mt: 10 }}>
            <Card>
                <CardContent sx={{mb:-1}}>
                    <Stack direction='row' alignItems='center'  sx={{ justifyContent: 'space-between',  mb: 2 }}>
                        <Stack direction="row" alignItems='center'  sx={{ justifyContent: 'space-between', width: '100%'}}>
                            <SearchField  />
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox checked={etroTeam} />}
                                    label="Not ETRO Team"
                                    componentsProps={{
                                        typography: {
                                          sx: { fontSize: 14 }
                                        }
                                      }}
                                    onChange={(event) => {
                                        AppStore.update((s) => {
                                            s.etroTeam = event.target.checked
                                            s.strengthsChecked = false;
                                            s.missingInfo = false;
                                        })
                                    }} />
                            </FormGroup>
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox checked={strengthsChecked} />}
                                    label="No Clifton Strengths"
                                    componentsProps={{
                                        typography: {
                                          sx: { fontSize: 14 }
                                        }
                                      }}
                                    onChange={(event) => {
                                        AppStore.update((s) => {
                                            s.strengthsChecked = event.target.checked;
                                            s.etroTeam = false;
                                            s.missingInfo = false;
                                        })
                                    }} />
                            </FormGroup>
                            <FormGroup>
                                <FormControlLabel
                                    control={<Checkbox checked={missingInfo} />}
                                    label="Missing Info"
                                    componentsProps={{
                                        typography: {
                                          sx: { fontSize: 14 }
                                        }
                                      }}
                                    onChange={(event) => {
                                        AppStore.update((s) => {
                                            s.missingInfo = event.target.checked;
                                            s.etroTeam = false;
                                            s.strengthsChecked = false;
                                        })
                                    }} />
                            </FormGroup>
                            <Typography sx={{ fontSize: '10px' }} variant="p" color='info'>
                                {`Last updated at: ${lastRunGetUsers}`}
                                <AsyncIconButton onClick={handleIconClick} >
                                    <SyncIcon sx={{ fontSize: '16px' }} />
                                </AsyncIconButton>
                            </Typography>
                            {/* <Chip label={`Last updated at: ${lastRunGetUsers}`} variant="outlined" color='info' /> */}
                        </Stack>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '16px', mx: 2 }}>
                            {getChip()}
                        </Box>
                        {user.is_lifecycle_admin && (
                            <Box>
                                <IconButton target='_blank' href={`mailto:${emailsSelected.join(', ')}`} disabled={emailsSelected.length === 0}>
                                    <EmailIcon />
                                </IconButton>
                            </Box>
                        )}

                    </Stack>
                    <Box style={{ width: '100%' }}>
                        <DataGrid
                            checkboxSelection={user.is_lifecycle_admin ? true : false}
                            disableColumnResize
                            rows={rows}
                            columns={employeesColumns}
                            onRowClick={(e) => handleUserToProfile(e)}
                            loading={loading}
                            localeText={{
                                noRowsLabel: 'No rows',
                            }}
                            slotProps={{
                                loadingOverlay: {
                                    variant: 'skeleton',
                                    noRowsVariant: 'skeleton'
                                }
                            }}
                            disableRowSelectionOnClick
                            paginationModel={paginationModel}
                            onPaginationModelChange={(model) => {
                                setPaginationModel((prev) => ({ ...prev, pageSize: model.pageSize, page: model.page }))
                                setNewPageSize(model.pageSize)
                            }}
                            onRowSelectionModelChange={(newRowSelectionModel) => {
                                getEmailsSelected(newRowSelectionModel)
                                setRowSelectionModel(newRowSelectionModel);
                            }}
                            rowSelectionModel={rowSelectionModel}
                            onFilterModelChange={(model) => {
                                model?.items[0]?.value ? setFilterUsed(true) : setFilterUsed(false)
                            }}
                            pageSizeOptions={[10, 15, 20]}
                            sx={dataGridStyles}
                        />
                    </Box>
                </CardContent>
            </Card>
        </Container>
    )
}

export default ListUsersWithDataGrid